import type { ComponentChildren } from "preact";
import { useRef, useState } from "preact/hooks";

/**
 * Lightweight hover tooltip: wraps a target element and shows rich `content` in a
 * floating panel above it on hover/focus. Used for relics, build slots, and stats so
 * we can show more than the native `title` attribute (descriptions + gain breakdowns).
 */
export function Tooltip({
  content,
  children,
  width = 200,
}: {
  content: ComponentChildren;
  children: ComponentChildren;
  width?: number;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: presentational hover wrapper; the child holds the real control, hover only toggles a tooltip.
    <span
      ref={ref}
      class="relative inline-flex"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocusIn={() => setOpen(true)}
      onFocusOut={() => setOpen(false)}
    >
      {children}
      {open && (
        <span
          role="tooltip"
          class="pointer-events-none absolute bottom-full left-1/2 z-50 mb-2 -translate-x-1/2 rounded-lg border border-edge bg-[#0b0f17]/97 px-3 py-2 text-left text-xs leading-snug shadow-xl shadow-black/50 backdrop-blur-sm"
          style={{ width: `${width}px` }}
        >
          {content}
          <span class="absolute left-1/2 top-full -ml-1 h-2 w-2 -translate-y-1 rotate-45 border-b border-r border-edge bg-[#0b0f17]" />
        </span>
      )}
    </span>
  );
}
